import { useState } from 'react';
import { isAxiosError } from 'axios';
import styled from 'styled-components';

import { useMutation } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import StandardModal, { Actions } from 'components/shared/StandardModal';
import { getPortalCredentialPublicKey, savePortalCredentials } from 'services/api/portalCredentials';
import { encryptCredentialEnvelope } from 'services/crypto/credentialEnvelope';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';

import { sourceSystemLabel } from './constants';

type Props = {
  sourceSystem: string;
  setShow: (show: boolean) => void;
};

/**
 * Save (or replace) the portal login headless automation signs in with
 * (OLIO-2886). The password never leaves this component unencrypted: it is
 * envelope-encrypted in the browser under the integration platform's public key
 * before the save call, so no Olio server can read it — only the headless worker
 * that types it into the portal.
 *
 * Saving is last-writer-wins per (client, portal): these become THE credentials
 * for the client's headless runs, and headless replays the saver's newest
 * recorded script with them.
 */
export default function PortalCredentialsModal({ sourceSystem, setShow }: Props) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const { addToast } = useToastActions();

  const sourceLabel = sourceSystemLabel(sourceSystem);

  const cancel = () => {
    setShow(false);
    setUsername('');
    setPassword('');
    setUsernameError('');
    setPasswordError('');
    setSubmitError('');
  };

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const key = await getPortalCredentialPublicKey();
      const credentialBlob = await encryptCredentialEnvelope({ username, password }, key.publicKey, key.keyId);
      return savePortalCredentials({ sourceSystem, username, credentialBlob });
    },
    onSuccess: () => {
      addToast({ text: `${sourceLabel} sign-in saved. Olio will verify it works shortly.` });
      cancel();
    },
    onError: (error) => {
      // The one expected 422: saving credentials before recording a script — the
      // server phrases it actionably, so surface its words.
      if (isAxiosError(error) && error.response?.status === 422) {
        // OliveBranch camelCases the error hash's keys on the way out (verified
        // against the live response), so the field key arrives as sourceSystem.
        const serverMessage = error.response.data?.errors?.sourceSystem?.[0];
        setSubmitError(serverMessage ?? 'Olio could not save this sign-in.');
        return;
      }
      setSubmitError('Olio could not save this sign-in. Try again in a moment.');
    },
  });

  const submit = () => {
    setUsernameError(username ? '' : 'This field is required');
    setPasswordError(password ? '' : 'This field is required');
    setSubmitError('');
    if (!username || !password) return;
    mutate();
  };

  return (
    <StandardModal title={`${sourceLabel} sign-in for automatic retrieval`} onCancel={cancel}>
      <Explainer>
        Olio signs into {sourceLabel} with this login when it retrieves referrals automatically. It is encrypted in your
        browser before it is saved — no one at Olio can read it. Saving replaces any login saved before, and automatic
        runs will use your newest recorded script.
      </Explainer>
      <FormContainer>
        <InputGroup title={`${sourceLabel} username`} error={usernameError}>
          <Input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete='off'
            data-testid='portal-credentials-username'
          />
        </InputGroup>
        <InputGroup title={`${sourceLabel} password`} error={passwordError}>
          <Input
            type='password'
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete='new-password'
            data-testid='portal-credentials-password'
          />
        </InputGroup>
      </FormContainer>
      {submitError && <SubmitError data-testid='portal-credentials-error'>{submitError}</SubmitError>}
      <Actions>
        <Button variant='ghost' onClick={cancel}>
          Cancel
        </Button>
        <Button onClick={submit} loading={isPending} data-testid='portal-credentials-save'>
          Save sign-in
        </Button>
      </Actions>
    </StandardModal>
  );
}

const Explainer = styled.p`
  font-size: 14px;
  color: ${colors.black75};
  margin: 0 0 16px;
  line-height: 1.4;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SubmitError = styled.p`
  font-size: 13px;
  color: ${colors.red100};
  margin: 8px 0 0;
`;
